import { Injectable, Injector } from '@angular/core';
import { Router, GuardsCheckEnd } from '@angular/router';
import { AuthenticationResource } from '../resources/authentication.resource';
import { Storage } from '../../util/storage';
import { HeaderInjector } from '../../http/InjectionBundle';
import { JwtHelperService } from '@auth0/angular-jwt';
import { PasswordResetResource } from '../resources/password-reset.resource';
import { CanActivateWithRedirect } from '../can-activate-with-redirect.interface';
import { AuthGuard } from '../auth.guard';

@Injectable()
export class AuthenticationService implements HeaderInjector {
	@Storage({
		scopeByTargetClass: true,
		storageResolver: { storage: 'local', storageKey: 'tokenStorage', defaultStorage: 'session' },
	})
	private token: any;

	private _redirectUrl?: string = undefined; // Route set after login
	private readonly _defaultRedirect = '/'; // Default to home

	constructor(
		private authenticationResource: AuthenticationResource,
		private passwordResetResource: PasswordResetResource,
		private router: Router,
		private injector: Injector
	) {
		this.initGuardNavigation();
	}

	// Route can be set by a route guard to have it be recovered after login
	public set redirectUrl(url: string | undefined) {
		this._redirectUrl = url;
	}

	public get redirectUrl(): string {
		return this._redirectUrl || this._defaultRedirect;
	}

	public async getHeader() {
		return Promise.resolve({ authorization: 'Bearer ' + this.token });
	}

	async login(credentials: { username: string; password: string }) {
		const token = await this.authenticationResource.login(undefined, credentials);

		if (!!token?.body?.token) {
			this.token = token.body.token;
		}

		return this.token;
	}

	getTokenData() {
		if (this.token !== undefined && this.token !== null) {
			const helper = new JwtHelperService();
			return helper.decodeToken(this.token);
		}
	}

	isAuthenticated(): boolean {
		if (this.token) {
			const auth = this.getTokenData();
			return (
				auth.exp > Date.now() / 1000 &&
				!!auth.data?.roles?.find((r: any) => r.name === 'SUPER_ADMIN' || 'ADMIN')
			);
		}
		return false;
	}

	async requestPasswordReset(emailOrUsername: string) {
		return this.passwordResetResource.requestReset({ emailorusername: emailOrUsername });
	}

	async updatePassword(token: string, updatedPassword: string) {
		return this.passwordResetResource.updatePassword({ token }, { updatedPassword });
	}

	public getToken(): string {
		return this.token;
	}

	async logout() {
		this.token = null;
	}

	isSuperAdmin(): boolean {
		const tokenData = this.getTokenData();
		if (!tokenData?.data?.roles) {
			return false;
		}

		return tokenData.data.roles.some((role: any) => role.name === 'SUPER_ADMIN');
	}

	/**
	 * Initializes guard navigation handling by subscribing to router events
	 * and redirecting users when guards deny access.
	 * Should be called once during application initialization (e.g., in AppComponent).
	 */
	private initGuardNavigation(): void {
		this.router.events.subscribe((event) => {
			if (event instanceof GuardsCheckEnd && !event.shouldActivate) {
				const redirectUrl = this.getRedirectUrlFromGuards();
				setTimeout(() => {
					this.router.navigateByUrl(redirectUrl);
				}, 100);
			}
		});
	}

	/**
	 * Gets the redirect URL from guards that implement CanActivateWithRedirect.
	 * Falls back to '/login' if no redirect URL is provided.
	 */
	private getRedirectUrlFromGuards(): string {
		try {
			// Get the AuthGuard instance
			const authGuard = this.injector.get(AuthGuard);

			// Check if it implements CanActivateWithRedirect
			if (authGuard && typeof (authGuard as any).redirectRoute === 'function') {
				const redirectUrl = (authGuard as CanActivateWithRedirect)?.redirectRoute();

				// Return the guard's redirect URL, or fall back to /login
				return redirectUrl || '/login';
			}
		} catch (error) {
			console.warn('Could not get redirect URL from guard:', error);
		}

		// Default fallback
		return '/login';
	}
}
